/**
 * Wall-clock times in a named time zone, for date-time inputs that should mean
 * the admin's zone (their profile's) rather than the browser's. A
 * `datetime-local` value is 'YYYY-MM-DDTHH:mm'.
 */

/** How far `timeZone` is ahead of UTC at `utcMs`, in ms. */
function offsetAt(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/** A `datetime-local` value read as a wall-clock time in `timeZone`, as epoch ms; null when it isn't one. */
export function zonedLocalToUtc(local: string, timeZone: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  // Twice, so a time near a daylight-saving change takes the offset in force then.
  let utc = wall - offsetAt(wall, timeZone);
  utc = wall - offsetAt(utc, timeZone);
  return Number.isFinite(utc) ? utc : null;
}

/** Epoch ms as a `datetime-local` value: the wall-clock time in `timeZone`. */
export function utcToZonedLocal(utcMs: number, timeZone: string): string {
  const wall = new Date(utcMs + offsetAt(utcMs, timeZone));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${wall.getUTCFullYear()}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}`;
}
